import { Box, Paper, Typography, Stack, Chip, alpha, useTheme } from "@mui/material";
import {
    Hotel,
    DirectionsCar,
    LocalActivity,
    AccessTime,
    LocationOn,
    Schedule,
    Flight,
    Star as StarIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const ProgramProductItem = ({ product, index }) => {
    const { i18n } = useTranslation();
    const lang = i18n.language;
    const theme = useTheme();

    const getIcon = () => {
        if (product.category_id === 3) return <Hotel fontSize="medium" sx={{ fontSize: 20 }} />;
        if (product.category_id === 2) return <DirectionsCar fontSize="medium" sx={{ fontSize: 20 }} />;
        if (product.product_type === 'flight') return <Flight fontSize="medium" sx={{ fontSize: 20 }} />;
        return <LocalActivity fontSize="medium" sx={{ fontSize: 20 }} />;
    };

    const getColor = () => {
        if (product.category_id === 3) return "info";
        if (product.category_id === 2) return "warning";
        return "success";
    };
    const renderFlightRoute = () => {
        const depCity =
            product.flight_details?.departure_airport?.city?.name?.[lang];

        const arrCity =
            product.flight_details?.arrival_airport?.city?.name?.[lang];

        if (!depCity || !arrCity) return null;

        return `${depCity} → ${arrCity}`;
    };


    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                display: "flex",
                gap: 2,
                "@media print": {
                    pageBreakInside: "avoid",
                    breakInside: "avoid",
                },
                "&:hover": {
                    bgcolor: "action.hover",
                },
                alignItems: "center",
            }}
        >
            {/* Index */}
            <Box
                sx={{
                    minWidth: 40,
                    height: 40,
                    borderRadius: "50%",
                    bgcolor: `${getColor()}.main`,
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    fontSize: "1rem",
                }}
            >
                {index}
            </Box>

            {/* Details */}
            <Box sx={{ flex: 1 }}>
                <Stack direction="row" justifyContent={'space-between'} spacing={1} flexWrap="wrap" useFlexGap>
                    <Stack direction="row" spacing={1} sx={{ flex: 1 }} alignItems="center" mb={0.5}>
                        {getIcon()}
                        <Typography variant="h6" fontWeight="600" sx={{ fontSize: '16px' }}>
                            {product?.product_type === 'hotel' ? product.hotel_details.hotel_name : product.title[lang]}
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ flex: 1, justifyContent: "flex-end" }} flexWrap="wrap" useFlexGap>
                        {product.time && (
                            <Chip
                                icon={<AccessTime sx={{ fontSize: 16 }} />}
                                label={product.time}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '12px', fontWeight: 500 }}
                            />
                        )}
                        {product.pickup_time && (
                            <Chip
                                icon={<Schedule sx={{ fontSize: 16 }} />}
                                label={product.pickup_time}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '12px', fontWeight: 500 }}
                            />
                        )}
                        {product.product_type === "flight" && (
                            <Chip
                                icon={<Flight sx={{ fontSize: 16 }} />}
                                label={renderFlightRoute()}
                                size="small"
                                variant="outlined"
                                sx={{
                                    bgcolor: "rgba(25, 118, 210, 0.85)",
                                    color: "#fff",
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    "& .MuiChip-icon": {
                                        color: "#fff",
                                    },
                                }}
                            />
                        )}
                        {product.city && (
                            <Chip
                                icon={<LocationOn color="white" sx={{ color: "#fff", fontSize: 16 }} />}
                                label={product.city?.name?.[lang] || product.city?.name?.en || product.city?.name?.ar || (typeof product.city === 'string' ? product.city : '')}
                                size="small"
                                variant="outlined"
                                sx={{ bgcolor: "rgba(30, 136, 229, 0.8)", color: "#fff", fontSize: '12px', fontWeight: 500 }}
                            />
                        )}
                        {product.units && (
                            <Chip
                                label={`${product.units} ${lang === 'ar' ? 'وحدة' : 'Units'}`}
                                size="small"
                                color={getColor()}
                                sx={{ fontSize: '12px', fontWeight: 500 }}
                            />
                        )}
                    </Stack>
                </Stack>

                {/* Hotel Details */}
                {product.hotel_details && (
                    <Box
                        sx={{
                            mt: 1.5,
                            p: 1.5,
                            borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.info.main, 0.06),
                            border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '14px' }}>
                                🏨 {/*  {product.hotel_details.hotel_name} */}
                            </Typography>
                            {product.hotel_details.rating && (
                                <Chip
                                    icon={<StarIcon sx={{ fontSize: 14 }} />}
                                    label={`${product.hotel_details.rating.stars || product.hotel_details.rating.name?.[lang] || ''} ★`}
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    sx={{ fontSize: '11px', fontWeight: 500 }}
                                />
                            )}
                        </Stack>

                        {/* Room stays for room_night_daily hotels */}
                        {product.hotel_details.room_stays?.length > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                                {product.hotel_details.room_stays
                                    .filter(rs => {
                                        const dayDate = product._dayKey?.split('_').slice(-1)[0] || product.date;
                                        return rs.date === dayDate;
                                    })
                                    .map((rs, i) => (
                                        <Stack key={i} direction="row" alignItems="center" spacing={1} sx={{ px: 0.5, py: 0.25 }}>
                                            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'info.dark' }}>
                                                🛏️ {rs.room_category?.name?.[lang] || `Room ${i + 1}`} × {rs.rooms_count || 1}
                                            </Typography>
                                        </Stack>
                                    ))}
                            </Box>
                        )}

                        {/* Meal plan and check-in/out for legacy hotels */}
                        {!product.hotel_details.room_stays && product.check_in_time && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                                {product.check_in_time} → {product.check_out_time}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default ProgramProductItem;
